import type { TournamentDivision, TournamentMatch } from "./api";
import { canonicalCourt, parseHalfCourt } from "./courts";
import { isScheduleHiddenFromPlayers, isScheduleUnpublished } from "./matchScheduleStatus";
import { escapeHtml } from "./printTemplate";
import { parseTimeMinutes, timeFromMinutes } from "./scheduleGrid";
import { divisionAccentColor } from "./tournamentDivisions";

// «El día completo con todas las categorías» (Alex Zayat vía Eduardo,
// 2026-10-02: «si quieres ver el calendario por día de un torneo completo con
// todas las categorías»). El calendario de una categoría ya pinta a las demás
// como ocupación; aquí vive la lógica pura de la vista «Todas las
// categorías»: el rol de juego del día (hora → cancha → categoría → partido)
// para la lista del celular y el PDF, y lo que falta publicar de cada
// categoría ese día. Se prueba sin montar la página.

type DivisionInfo = Pick<TournamentDivision, "id" | "name"> &
  Partial<Pick<TournamentDivision, "code" | "sort_order">>;

/** Nombre corto de una categoría para las tarjetas del grid: su clave si la
 * tiene («VA»), si no su nombre. */
export function divisionShortName(division: Pick<TournamentDivision, "name"> & Partial<Pick<TournamentDivision, "code">> | null | undefined): string {
  if (!division) return "";
  return division.code?.trim() || division.name.trim();
}

export interface DayRollRow {
  match: TournamentMatch;
  /** "HH:MM"; "" si el partido tiene fecha pero no hora. */
  time: string;
  startMin: number | null;
  /** Cancha guardada ("3", "9-A", «Central»); "" sin cancha. */
  court: string;
  /** Sede adicional; null = la principal. */
  venue: string | null;
  divisionId: number | null;
  divisionName: string;
  /** El jugador aún no ve este horario (sin publicar). */
  hiddenFromPlayers: boolean;
}

function courtSortKey(value: string): { number: number | null; base: string; half: string } {
  const half = parseHalfCourt(value);
  const base = half ? half.base : canonicalCourt(value) ?? "";
  return {
    number: /^\d+$/.test(base) ? Number(base) : null,
    base: base.toLocaleLowerCase("es-MX"),
    half: half?.half ?? "",
  };
}

/** Orden de canchas del rol: 1, 2, …, 9, 9-A, 9-B, 10; después las de nombre
 * («Central»); sin cancha al final. */
export function compareCourtNames(a: string, b: string): number {
  const left = courtSortKey(a);
  const right = courtSortKey(b);
  if (!left.base || !right.base) return left.base ? -1 : right.base ? 1 : 0;
  if (left.number !== null && right.number !== null) {
    return left.number - right.number || left.half.localeCompare(right.half);
  }
  if (left.number !== null) return -1;
  if (right.number !== null) return 1;
  return (
    left.base.localeCompare(right.base, "es-MX", { numeric: true, sensitivity: "base" }) ||
    left.half.localeCompare(right.half)
  );
}

/** Orden de las categorías: el de la configuración (sort_order) y su nombre. */
export function divisionOrder(divisions: DivisionInfo[]): Map<number, number> {
  const sorted = [...divisions].sort(
    (a, b) =>
      (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
      a.name.localeCompare(b.name, "es-MX", { numeric: true, sensitivity: "base" }),
  );
  return new Map(sorted.map((division, index) => [division.id, index]));
}

/** Partidos del día en el orden del rol de juego: hora → sede → cancha →
 * categoría → número de partido. Entran los que tienen esa fecha y no están
 * cancelados (también los ya jugados y los pospuestos); los que no tienen
 * hora van al final. Un partido repetido (la categoría abierta y la lista
 * del torneo) cuenta una vez. */
export function dayRollRows(
  matches: TournamentMatch[],
  options: { date: string; divisions: DivisionInfo[] },
): DayRollRow[] {
  const byId = new Map(options.divisions.map((division) => [division.id, division]));
  const order = divisionOrder(options.divisions);
  const seen = new Set<number>();
  const rows: DayRollRow[] = [];
  for (const match of matches) {
    if (seen.has(match.id)) continue;
    seen.add(match.id);
    if (match.scheduled_date !== options.date || match.status === "cancelled") continue;
    const startMin = parseTimeMinutes(match.scheduled_time);
    const division = match.division_id != null ? byId.get(match.division_id) : undefined;
    rows.push({
      match,
      time: startMin === null ? "" : timeFromMinutes(startMin),
      startMin,
      court: canonicalCourt(match.court) ?? "",
      venue: match.venue_name?.trim() || null,
      divisionId: match.division_id ?? null,
      divisionName: division?.name ?? (match.division_name?.trim() || "Sin categoría"),
      hiddenFromPlayers: isScheduleHiddenFromPlayers(match),
    });
  }
  const divisionRank = (row: DayRollRow) =>
    row.divisionId != null ? order.get(row.divisionId) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
  return rows.sort(
    (a, b) =>
      (a.startMin ?? Number.MAX_SAFE_INTEGER) - (b.startMin ?? Number.MAX_SAFE_INTEGER) ||
      (a.venue === b.venue ? 0 : a.venue === null ? -1 : b.venue === null ? 1 : a.venue.localeCompare(b.venue, "es-MX")) ||
      compareCourtNames(a.court, b.court) ||
      divisionRank(a) - divisionRank(b) ||
      a.divisionName.localeCompare(b.divisionName, "es-MX") ||
      (a.match.match_number ?? Number.MAX_SAFE_INTEGER) - (b.match.match_number ?? Number.MAX_SAFE_INTEGER) ||
      a.match.id - b.match.id,
  );
}

export interface DayRollSlot {
  /** "HH:MM" o "" (hora por definir). */
  time: string;
  rows: DayRollRow[];
}

/** El rol agrupado por hora (las filas ya vienen en orden). */
export function dayRollSlots(rows: DayRollRow[]): DayRollSlot[] {
  const slots: DayRollSlot[] = [];
  for (const row of rows) {
    const last = slots[slots.length - 1];
    if (last && last.time === row.time) last.rows.push(row);
    else slots.push({ time: row.time, rows: [row] });
  }
  return slots;
}

export interface DivisionDayPending {
  divisionId: number;
  name: string;
  /** Horarios sin publicar de la categoría ese día (todas sus sedes). */
  count: number;
  matches: TournamentMatch[];
}

/** Lo que falta publicar ese día, por categoría (en el orden de las
 * categorías). Es lo mismo que publica «Publicar horarios» de cada una:
 * por jugarse, sin publicar y con esa fecha, en todas las sedes. */
export function dayPendingByDivision(
  matches: TournamentMatch[],
  date: string,
  divisions: DivisionInfo[],
): DivisionDayPending[] {
  const byId = new Map(divisions.map((division) => [division.id, division]));
  const order = divisionOrder(divisions);
  const seen = new Set<number>();
  const groups = new Map<number, DivisionDayPending>();
  for (const match of matches) {
    if (seen.has(match.id)) continue;
    seen.add(match.id);
    if (match.division_id == null || match.scheduled_date !== date || !isScheduleUnpublished(match)) continue;
    const entry = groups.get(match.division_id) ?? {
      divisionId: match.division_id,
      name: byId.get(match.division_id)?.name ?? (match.division_name?.trim() || `Categoría ${match.division_id}`),
      count: 0,
      matches: [],
    };
    entry.count += 1;
    entry.matches.push(match);
    groups.set(match.division_id, entry);
  }
  return [...groups.values()].sort(
    (a, b) =>
      (order.get(a.divisionId) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.divisionId) ?? Number.MAX_SAFE_INTEGER) ||
      a.name.localeCompare(b.name, "es-MX"),
  );
}

/** Estilos del PDF del rol: cada hora abre con una raya más gruesa. */
export const DAY_ROLL_PRINT_CSS = `
    .print-sheet table.day-roll { table-layout: fixed; }
    .print-sheet table.day-roll td { overflow: hidden; text-overflow: ellipsis; }
    .print-sheet table.day-roll tr.is-slot-start td { border-top: 2px solid #6f9a58; }
    .print-sheet table.day-roll td.day-roll__time { font-weight: 800; white-space: nowrap; }
    .print-sheet table.day-roll td.day-roll__division { border-left-width: 4px; font-weight: 700; }
    .print-sheet table.day-roll .day-roll__draft { display: inline-block; padding: 0 5px; border: 1px solid #5a2fc2; border-radius: 4px; color: #5a2fc2; font-size: 0.78em; font-weight: 800; }
    .print-sheet tr.print-line-row td { font-size: 0.86em; color: #3f4a3a; background: #fafcf6; }
    .print-sheet tr.print-line-row td.print-line-label { padding-left: 16px; }
`;

export interface DayRollPrintOptions {
  /** "published-only": sin los que el jugador aún no ve; "mark": todos, con
   * la marca «Sin publicar»; "none": no hay sin publicar. */
  drafts: "none" | "published-only" | "mark";
  /** Torneo con varias sedes: la cancha lleva su sede. */
  showVenue: boolean;
  roundLabel: (match: TournamentMatch) => string;
  matchLabel: (match: TournamentMatch) => string;
  /** Resultado o estado (jugado, pospuesto, en juego); "" si nada. */
  note: (match: TournamentMatch) => string;
  /** Líneas de una serie por equipos (Mini Copa). */
  tieLines?: (match: TournamentMatch) => Array<{ line: TournamentMatch; label: string }>;
  lineNote?: (line: TournamentMatch) => string;
}

/** Filas que entran al PDF según la opción de los sin publicar. */
export function printableDayRollRows(rows: DayRollRow[], drafts: DayRollPrintOptions["drafts"]): DayRollRow[] {
  return drafts === "published-only" ? rows.filter((row) => !row.hiddenFromPlayers) : rows;
}

function courtCell(row: Pick<DayRollRow, "court" | "venue">, showVenue: boolean): string {
  const court = row.court || "Por definir";
  return showVenue && row.venue ? `${court} · ${row.venue}` : court;
}

/** Cuerpo HTML del PDF «Rol de juego del día»: una tabla en orden de hora y
 * cancha, con la categoría (y su color), la ronda o grupo, el partido y una
 * nota (resultado, pospuesto o «Sin publicar»). */
export function dayRollPrintHtml(rows: DayRollRow[], options: DayRollPrintOptions): string {
  const printable = printableDayRollRows(rows, options.drafts);
  let previousTime: string | null = null;
  const body = printable
    .map((row) => {
      const slotStart = row.time !== previousTime;
      previousTime = row.time;
      const color = row.divisionId != null ? divisionAccentColor({ id: row.divisionId, name: row.divisionName }) : "#9aa89a";
      const draft = options.drafts === "mark" && row.hiddenFromPlayers ? `<span class="day-roll__draft">Sin publicar</span>` : "";
      const note = escapeHtml(options.note(row.match));
      const lines = (options.tieLines?.(row.match) ?? [])
        .map(({ line, label }) => {
          const lineTime = parseTimeMinutes(line.scheduled_time);
          const lineCourt = canonicalCourt(line.court) ?? "";
          return `<tr class="print-line-row">
          <td>${escapeHtml(lineTime !== null && timeFromMinutes(lineTime) !== row.time ? timeFromMinutes(lineTime) : "")}</td>
          <td>${escapeHtml(lineCourt && lineCourt !== row.court ? lineCourt : "")}</td>
          <td></td>
          <td></td>
          <td class="print-line-label">${escapeHtml(`↳ ${label}`)}</td>
          <td>${escapeHtml(options.lineNote?.(line) ?? "")}</td>
        </tr>`;
        })
        .join("");
      return `<tr class="day-roll__row${slotStart ? " is-slot-start" : ""}" data-roll-match="${row.match.id}">
          <td class="day-roll__time">${escapeHtml(row.time || "Por definir")}</td>
          <td>${escapeHtml(courtCell(row, options.showVenue))}</td>
          <td class="day-roll__division" style="border-left-color:${color}">${escapeHtml(row.divisionName)}</td>
          <td>${escapeHtml(options.roundLabel(row.match) || "—")}</td>
          <td>${escapeHtml(options.matchLabel(row.match))}</td>
          <td>${[draft, note].filter(Boolean).join(" ")}</td>
        </tr>${lines}`;
    })
    .join("");
  return `
      <table class="day-roll">
        <colgroup><col style="width:8%"/><col style="width:${options.showVenue ? 15 : 9}%"/><col style="width:15%"/><col style="width:16%"/><col style="width:${options.showVenue ? 30 : 36}%"/><col style="width:16%"/></colgroup>
        <thead><tr><th>Hora</th><th>Cancha</th><th>Categoría</th><th>Ronda / grupo</th><th>Partido</th><th>Nota</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
}
